import { Check, Building2 } from 'lucide-react';

export function PricingPage() {
  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-50 to-white py-12">
      <div className="container mx-auto px-4">
        {/* Header Section */}
        <div className="mx-auto max-w-4xl text-center">
          <div className="mb-4 inline-flex items-center rounded-lg bg-muted px-3 py-1 text-sm font-medium">
            <Building2 className="mr-2 h-4 w-4" />
            Enterprise Solutions
          </div>
          <h2 className="text-base font-semibold leading-7 text-primary">
            Pricing
          </h2>
          <p className="mt-2 text-4xl font-bold tracking-tight text-foreground sm:text-5xl">
            Built for Large Organizations
          </p>
          <p className="mt-6 text-lg leading-8 text-muted-foreground">
            Streamline your hiring process with our comprehensive enterprise platform. 
            Get 100 user licenses with 1,000 analyses each, plus advanced features designed for scale.
          </p>
        </div>

        {/* Key Benefits */}
        <div className="mx-auto mt-16 max-w-2xl rounded-3xl ring-1 ring-gray-200 sm:mt-20 lg:mx-0 lg:flex lg:max-w-none">
          <div className="p-8 sm:p-10 lg:flex-auto">
            <h3 className="text-2xl font-bold tracking-tight text-foreground">
              Enterprise License Package
            </h3>
            <p className="mt-6 text-base leading-7 text-muted-foreground">
              Perfect for large organizations looking to transform their recruitment process 
              with AI-powered CV analysis and intelligent candidate matching.
            </p>
            <div className="mt-10 flex items-center gap-x-4">
              <h4 className="flex-none text-sm font-semibold leading-6 text-primary">
                What's included
              </h4>
              <div className="h-px flex-auto bg-gray-100" />
            </div>
            <ul role="list" className="mt-8 grid grid-cols-1 gap-4 text-sm leading-6 text-muted-foreground sm:grid-cols-2 sm:gap-6">
              {[
                '100 user licenses included',
                '1,000 analyses per license per month',
                'Advanced AI matching & insights',
                'Market intelligence & benchmarking',
                'Priority enterprise support',
                'Advanced analytics dashboard',
                'Custom integrations & API access',
                'Dedicated account manager'
              ].map((feature) => (
                <li key={feature} className="flex gap-x-3">
                  <Check className="h-6 w-5 flex-none text-primary" aria-hidden="true" />
                  {feature}
                </li>
              ))}
            </ul>
          </div>
          <div className="-mt-2 p-2 lg:mt-0 lg:w-full lg:max-w-md lg:flex-shrink-0">
            <div className="rounded-2xl bg-gray-50 py-10 text-center ring-1 ring-inset ring-gray-900/5 lg:flex lg:flex-col lg:justify-center lg:py-16">
              <div className="mx-auto max-w-xs px-8">
                <p className="text-base font-semibold text-muted-foreground">
                  Enterprise Package
                </p>
                <p className="mt-6 flex items-baseline justify-center gap-x-2">
                  <span className="text-5xl font-bold tracking-tight text-foreground">
                    Custom
                  </span>
                  <span className="text-sm font-semibold leading-6 tracking-wide text-muted-foreground">
                    pricing
                  </span>
                </p>
                <p className="mt-2 text-xs leading-5 text-muted-foreground">
                  Tailored to your organization's needs
                </p>
                <button className="mt-10 block w-full rounded-md bg-primary px-3 py-2 text-center text-sm font-semibold text-primary-foreground shadow-sm hover:bg-primary/90 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-primary">
                  Contact Sales
                </button>
                <p className="mt-6 text-xs leading-5 text-muted-foreground">
                  Custom onboarding & training included
                </p>
              </div>
            </div>
          </div>
        </div>

        {/* Stats Section */}
        <div className="mx-auto mt-16 max-w-7xl sm:mt-20">
          <div className="mx-auto max-w-2xl lg:mx-0">
            <h3 className="text-3xl font-bold tracking-tight text-foreground sm:text-4xl">
              Scale your hiring with confidence
            </h3>
            <p className="mt-6 text-lg leading-8 text-muted-foreground">
              Our enterprise platform is designed to handle the complexity and scale 
              of large organization recruitment needs.
            </p>
          </div>
          <dl className="mx-auto mt-16 grid max-w-2xl grid-cols-1 gap-x-8 gap-y-10 text-foreground sm:mt-20 sm:grid-cols-2 sm:gap-y-16 lg:mx-0 lg:max-w-none lg:grid-cols-4">
            <div className="flex flex-col gap-y-3 border-l border-gray-900/10 pl-6">
              <dt className="text-sm leading-6 text-muted-foreground">Monthly analyses included</dt>
              <dd className="order-first text-3xl font-semibold tracking-tight">100,000</dd>
            </div>
            <div className="flex flex-col gap-y-3 border-l border-gray-900/10 pl-6">
              <dt className="text-sm leading-6 text-muted-foreground">User licenses</dt>
              <dd className="order-first text-3xl font-semibold tracking-tight">100</dd>
            </div>
            <div className="flex flex-col gap-y-3 border-l border-gray-900/10 pl-6">
              <dt className="text-sm leading-6 text-muted-foreground">Per-license monthly limit</dt>
              <dd className="order-first text-3xl font-semibold tracking-tight">1,000</dd>
            </div>
            <div className="flex flex-col gap-y-3 border-l border-gray-900/10 pl-6">
              <dt className="text-sm leading-6 text-muted-foreground">Dedicated support</dt>
              <dd className="order-first text-3xl font-semibold tracking-tight">24/7</dd>
            </div>
          </dl>
        </div>

        {/* CTA Section */}
        <div className="mx-auto mt-16 max-w-2xl text-center sm:mt-20">
          <h3 className="text-2xl font-bold tracking-tight text-foreground">
            Ready to transform your hiring process?
          </h3>
          <p className="mt-6 text-lg leading-8 text-muted-foreground">
            Contact our sales team to discuss your organization's specific needs 
            and get a custom pricing proposal.
          </p>
          <div className="mt-8 flex items-center justify-center gap-x-6">
            <button className="rounded-md bg-primary px-3.5 py-2.5 text-sm font-semibold text-primary-foreground shadow-sm hover:bg-primary/90 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-primary">
              Schedule Demo
            </button>
            <a href="#" className="text-sm font-semibold leading-6 text-foreground">
              View Documentation <span aria-hidden="true">→</span>
            </a>
          </div>
        </div>
      </div>
    </div>
  );
}